import baseAPI from "@/api/base";
import axios, { AxiosProgressEvent, AxiosResponse } from "axios";
import { NoteAttachment } from "@/dtos/Note.dto";
import {
  NOTE_DRAFT_PRESIGN_URL,
  NOTE_DRAFT_CONFIRM_URL,
  NOTE_DRAFT_DELETE_URL,
  NOTE_PRESIGN_URL,
  NOTE_CONFIRM_URL,
  NOTE_ATTACHMENT_DELETE_URL,
} from "@/utils/urls";

export interface UploadOptions {
  onProgress?: (pct: number) => void;
  signal?: AbortSignal;
}

export interface PresignResponse {
  uploadUrl: string;
  storageKey: string;
  publicUrl: string;
  expiresIn: number;
}

type PresignAxiosResponse = AxiosResponse<{ status: boolean; data: PresignResponse; msg: string }>;
type ConfirmAxiosResponse = AxiosResponse<{ status: boolean; data: NoteAttachment; msg: string }>;
type SimpleAxiosResponse = AxiosResponse<{ status: boolean; data: null; msg: string }>;

// Isolated instance so global interceptors can't leak our Bearer token to Spaces.
const spacesHttp = axios.create({ timeout: 0, withCredentials: false });

// Fallback when browser reports empty file.type (some AVIs, M4As on old OSes).
const EXT_MIME: Record<string, string> = {
  mp3: "audio/mpeg",
  wav: "audio/wav",
  ogg: "audio/ogg",
  m4a: "audio/x-m4a",
  aac: "audio/aac",
  webm: "audio/webm",
  mp4: "video/mp4",
  mov: "video/quicktime",
  avi: "video/x-msvideo",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  gif: "image/gif",
  webp: "image/webp",
  svg: "image/svg+xml",
  bmp: "image/bmp",
  pdf: "application/pdf",
  txt: "text/plain",
};

export const inferMimeType = (file: File): string => {
  if (file.type) return file.type;
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  return EXT_MIME[ext] ?? "";
};

class NoteAttachmentService {
  static presignDraft = async (draftKey: string, file: File): Promise<PresignAxiosResponse> => {
    return baseAPI.post(NOTE_DRAFT_PRESIGN_URL, {
      draftKey,
      fileName: file.name,
      fileType: inferMimeType(file),
      fileSize: file.size,
    });
  };

  static presignNote = async (noteId: string, file: File): Promise<PresignAxiosResponse> => {
    return baseAPI.post(NOTE_PRESIGN_URL(noteId), {
      fileName: file.name,
      fileType: inferMimeType(file),
      fileSize: file.size,
    });
  };

  static putToPresignedUrl = async (
    url: string,
    file: File,
    opts?: UploadOptions
  ): Promise<AxiosResponse<unknown>> => {
    try {
      return await spacesHttp.put(url, file, {
        headers: { "Content-Type": inferMimeType(file) },
        signal: opts?.signal,
        transformRequest: [(d: unknown) => d],
        onUploadProgress: (e: AxiosProgressEvent) => {
          if (!opts?.onProgress || !e.total) return;
          opts.onProgress(Math.min(100, Math.round((e.loaded / e.total) * 100)));
        },
      });
    } catch (err: any) {
      const body = err?.response?.data;
      if (typeof body === "string" && body.trimStart().startsWith("<?xml")) {
        try {
          const doc = new DOMParser().parseFromString(body, "application/xml");
          err.s3 = {
            code: doc.querySelector("Code")?.textContent ?? null,
            message: doc.querySelector("Message")?.textContent ?? null,
            httpStatus: err.response?.status ?? null,
          };
        } catch {
          /* XML parse failed — fall through without s3 annotation */
        }
      }
      throw err;
    }
  };

  static confirmDraft = async (
    draftKey: string,
    payload: { storageKey: string; fileName: string }
  ): Promise<ConfirmAxiosResponse> => {
    return baseAPI.post(NOTE_DRAFT_CONFIRM_URL, { draftKey, ...payload });
  };

  static confirmNote = async (
    noteId: string,
    payload: { storageKey: string; fileName: string }
  ): Promise<ConfirmAxiosResponse> => {
    return baseAPI.post(NOTE_CONFIRM_URL(noteId), payload);
  };

  static deleteDraft = async (draftKey: string): Promise<SimpleAxiosResponse> => {
    return baseAPI.delete(`${NOTE_DRAFT_DELETE_URL}/${encodeURIComponent(draftKey)}`);
  };

  static delete = async (noteId: string, attachmentId: string): Promise<SimpleAxiosResponse> => {
    return baseAPI.delete(NOTE_ATTACHMENT_DELETE_URL(noteId, attachmentId));
  };
}

export default NoteAttachmentService;
